import type {I18n} from "@lingui/core";
import {msg} from "@lingui/core/macro";
import {useLingui} from "@lingui/react";
import {Trans} from "@lingui/react/macro";
import {useMemo, useState, type ReactNode} from "react";
import {Link} from "react-router-dom";
import * as v from "valibot";
import {useSortedCategories} from "../../models/category-queries";
import type {
  CategoryId,
  GroupRepo,
  ItemEntry,
  ItemId,
  ItemVariantEntry,
  ItemVariantId,
} from "../../models/GroupRepo";
import {getVariantIdsByItemId} from "../../models/item-queries";
import {useUnitList} from "../../models/unit-queries";
import PlusMinusButtons from "../../shared/PlusMinusButtons";
import {greenTheme} from "../../styles/themes/color-themes.css";
import {Box, Col, css, Row, Text} from "../../ui/Box";
import Button from "../../ui/Button";
import Icon from "../../ui/Icon";
import {isEmoji} from "../../utils";
import {SelectField, TextField} from "../form/fields";
import {useForm} from "../form/Form";
import {quantitySchema} from "../form/form-utils";
import {addMessage} from "../messenger/messenger";

const Block = ({children, title}: {children: ReactNode; title: ReactNode}) => (
  <Col
    rounded="4"
    borderWidth={1}
    borderColor="default"
    pa="16"
    sp="12"
    width="100%"
    maxWidth="formWidth"
  >
    <Text as="h3" size="16">
      {title}
    </Text>
    {children}
  </Col>
);

const updatePropertiesFormSchema = v.object({
  categoryId: v.nullable(
    v.pipe(
      v.string(),
      v.transform((val) => val as CategoryId)
    )
  ),
  emoji: v.pipe(v.string(), v.check(isEmoji, "no_emoji")),
  itemName: v.pipe(v.string(), v.nonEmpty(), v.maxLength(256)),
});

type UpdatePropertiesProps = {
  item: ItemEntry;
  groupRepo: GroupRepo;
  i18n: I18n;
  onClose: () => void;
};
const UpdateProperties = ({item, i18n, groupRepo, onClose}: UpdatePropertiesProps) => {
  const {Form, field} = useForm({
    initial: {
      categoryId: item.categoryId,
      itemName: item.name,
      emoji: item.emoji || "",
    },
    submitOnBlur: true,
    schema: updatePropertiesFormSchema,
    onSubmit: ({categoryId, itemName, emoji}) => {
      groupRepo.Items.patch(item.id, {
        categoryId,
        name: itemName,
        emoji: emoji || null,
      });
    },
  });

  const group = groupRepo.vals.useValues();
  const cats = useSortedCategories(groupRepo, group.id);
  const [showDangerZone, setShowDangerZone] = useState(false);

  return (
    <Block title={<Trans>Update Properties</Trans>}>
      <Form>
        <TextField label="Name" field={field("itemName")} placeholder="Item name" />
        <TextField field={field("emoji")} label={<Trans>Emoji</Trans>} />
        <SelectField
          label="Category"
          field={field("categoryId")}
          options={[
            {label: "None", value: null},
            ...cats.map(({cat}) => ({
              label: `${cat.emoji} ${cat.name}`,
              value: cat.id,
            })),
          ]}
        />
      </Form>
      <Button
        onClick={() => setShowDangerZone(!showDangerZone)}
        className={css({alignSelf: "start"})}
        active={showDangerZone}
      >
        <Trans>Danger Zone</Trans>
      </Button>
      {showDangerZone && (
        <DangerZone item={item} groupRepo={groupRepo} onClose={onClose} i18n={i18n} />
      )}
    </Block>
  );
};

// only for extracting keys
const _message = [msg({id: "item.deleted"})];

const DangerZone = ({
  item,
  groupRepo,
  onClose,
  i18n,
}: {
  item: ItemEntry;
  groupRepo: GroupRepo;
  onClose: () => void;
  i18n: I18n;
}) => {
  const ingredients = Object.values(groupRepo.Ingredients.getAll());
  const allRecipes = groupRepo.Recipes.useGetAll();
  const {recipesUsingItem, itemIngredients} = useMemo(() => {
    const itemIngredients = ingredients.filter((i) => i.itemId === item.id);
    return {
      recipesUsingItem: itemIngredients.map((i) => allRecipes[i.recipeId]).filter(Boolean),
      itemIngredients,
    };
  }, [allRecipes, ingredients, item.id]);
  const handleDelete = () => {
    groupRepo.store.transaction(() => {
      const allVars = getVariantIdsByItemId(groupRepo, item.id);
      for (const varId of allVars) {
        groupRepo.ItemVariants.delete(varId);
      }
      groupRepo.Items.delete(item.id);
      for (const ing of itemIngredients) {
        groupRepo.Ingredients.delete(ing.id);
      }
    });
    addMessage(i18n._("item.deleted", {itemName: item.name}));
    onClose();
  };
  return (
    <Col sp="8">
      <Button onClick={handleDelete} disabled={recipesUsingItem.length > 0}>
        <Trans>Delete Item</Trans>
      </Button>
      {recipesUsingItem.length > 0 && (
        <Col sp="4">
          <Text>Can't delete item because it is used in these recipes:</Text>
          {recipesUsingItem.map((r) => (
            <Link
              key={r.id}
              to={`../cook/${r.id}`}
              className={css({
                borderWidth: 1,
                borderColor: "default",
                px: "16",
                py: "8",
                rounded: "4",
                display: "flex",
                align: "center",
              })}
            >
              {r.name}
            </Link>
          ))}
        </Col>
      )}
    </Col>
  );
};

const usePastValues = (itemId: ItemId, groupRepo: GroupRepo) => {
  return useMemo(() => {
    const variants = new Set<string>();
    const quants = new Map<string, {quantity: number; unit: string}>();
    const pastVariantIds = getVariantIdsByItemId(groupRepo, itemId).slice(0, 50);
    for (const id of pastVariantIds) {
      const pastVariant = groupRepo.ItemVariants.getById(id);
      if (pastVariant?.variant) variants.add(pastVariant.variant);
      if (pastVariant?.unit) {
        const key = `${pastVariant.quantity}:${pastVariant.unit}`;
        quants.set(key, {quantity: pastVariant.quantity, unit: pastVariant.unit});
      }
    }
    return {
      pastVariants: [...variants],
      pastQuantities: [...quants.values()],
    };
  }, [groupRepo, itemId]);
};

// eslint-disable-next-line react-refresh/only-export-components
export const usePastVariantValues = usePastValues;

const updateVariantFormSchema = v.object({variant: v.string()});

type UpdateVariantProps = {
  variant: ItemVariantEntry;
  groupRepo: GroupRepo;
  pastVariants: string[];
};
const UpdateVariant = (props: UpdateVariantProps) => {
  const {variant, groupRepo, pastVariants} = props;

  const handleVariantChange = (nextValue: string) => {
    groupRepo.ItemVariants.patch(variant.id, {variant: nextValue || null});
  };

  const handleVariantButton = (nextValue: string) => {
    setValue("variant", nextValue);
    handleVariantChange(nextValue);
  };

  const {Form, field, setValue} = useForm({
    initial: {
      variant: variant.variant || "",
    },
    submitOnBlur: true,
    schema: updateVariantFormSchema,
    onSubmit: ({variant: nextValue}) => {
      handleVariantChange(nextValue);
    },
  });

  return (
    <Block title={<Trans>Update Variant</Trans>}>
      <Form className={css({sp: "8", display: "flex", flexDir: "column"})}>
        <TextField field={field("variant")} label={<Trans>Variant</Trans>} />
        {pastVariants.length > 0 && (
          <Row wrap sp="4">
            {pastVariants.map((v) => (
              <Button
                key={v}
                active={v === variant.variant}
                size="sm"
                onClick={() => handleVariantButton(v === variant.variant ? "" : v)}
              >
                {v}
              </Button>
            ))}
          </Row>
        )}
      </Form>
    </Block>
  );
};

const updateQuantityFormSchema = v.object({
  quantity: quantitySchema,
  unit: v.nullable(v.string()),
});

const UpdateQuantity = ({
  variant,
  groupRepo,
  pastQuantities,
  i18n,
}: {
  variant: ItemVariantEntry;
  groupRepo: GroupRepo;
  pastQuantities: {quantity: number; unit: string}[];
  i18n: I18n;
}) => {
  const id = variant.id;

  const {Form, field, setValue} = useForm({
    initial: {
      quantity: variant.quantity.toString(),
      unit: variant.unit,
    },
    submitOnBlur: true,
    schema: updateQuantityFormSchema,
    onSubmit: ({unit, quantity}) => {
      groupRepo.ItemVariants.patch(id, {quantity, unit});
    },
  });
  const handleUpdate = (next: number) => {
    setValue("quantity", `${next}`);
    groupRepo.ItemVariants.patch(id, {
      completedAt: null,
      quantity: next,
    });
  };
  const handleDelete = () => {
    setValue("quantity", "0");
    groupRepo.ItemVariants.patch(id, {
      completedAt: new Date(),
    });
  };

  const handleButton = ({quantity, unit}: {quantity: number; unit: string}) => {
    setValue("quantity", quantity.toString());
    setValue("unit", unit);
    groupRepo.ItemVariants.patch(id, {
      quantity,
      unit,
    });
  };

  const units = useUnitList(groupRepo, i18n);

  return (
    <Row
      rounded="4"
      borderWidth={1}
      borderColor="default"
      pa="16"
      sp="8"
      align="start"
      width="100%"
      maxWidth="formWidth"
    >
      <Text as="h3" size="16" className={css({"@hide": "sm"})} flex="none">
        <Trans>Quantity</Trans>
      </Text>
      <Col>
        <Form className={css({display: "flex", sp: "8"})}>
          <TextField
            field={field("quantity")}
            label={null}
            type="number"
            min="0"
            className={css({width: "100%"})}
          />
          <SelectField field={field("unit")} label={null} options={units} />
        </Form>
        {pastQuantities.length > 0 && (
          <Row wrap sp="4">
            {pastQuantities.map(({quantity, unit}) => (
              <Button
                active={quantity === variant.quantity && unit === variant.unit}
                key={`${quantity}:${unit}`}
                size="sm"
                onClick={() => handleButton({quantity, unit})}
              >
                {quantity}
                {unit}
              </Button>
            ))}
          </Row>
        )}
      </Col>
      <PlusMinusButtons
        className={css({ml: "auto"})}
        value={variant.completedAt !== null ? 0 : variant.quantity}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
      />
    </Row>
  );
};

type RefineItemProps = {
  id: ItemVariantId;
  justAdded?: null | "new" | "existing";
  onClose: () => void;
  groupRepo: GroupRepo;
};
const RefineItem = (props: RefineItemProps) => {
  const {id, justAdded, onClose, groupRepo} = props;
  const variant = groupRepo.ItemVariants.useById(id);
  const item = groupRepo.Items.useById(variant?.itemId as ItemId);
  const {i18n} = useLingui();
  const {pastQuantities, pastVariants} = usePastValues(variant?.itemId as ItemId, groupRepo);

  if (!item || !variant) return <div>No such item</div>;

  return (
    <Col sp="16" align="center">
      {justAdded && (
        <Row className={greenTheme} bg="surface" rounded="4" pa="8" sp="8" align="center">
          <Icon name="check" className={css({color: "secondary"})} />
          <Box color="primary" size="12">
            {item.name} was added
          </Box>
        </Row>
      )}

      <Text
        as="h2"
        size="20"
        textOverflow="ellipsis"
        overflow="hidden"
        textWrap="nowrap"
        maxWidth="100%"
      >
        <Trans>Refine {item.name}</Trans>
      </Text>
      <UpdateQuantity
        variant={variant}
        groupRepo={groupRepo}
        pastQuantities={pastQuantities}
        i18n={i18n}
      />
      <UpdateVariant variant={variant} groupRepo={groupRepo} pastVariants={pastVariants} />
      {justAdded !== "existing" && (
        <UpdateProperties item={item} groupRepo={groupRepo} i18n={i18n} onClose={onClose} />
      )}
    </Col>
  );
};

export default RefineItem;
